import { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import {
  tokenAdminService,
  AdminTokenStats,
  AdminTokenPackage,
  AdminAIService,
  AdminTokenTransaction,
} from '../../services/token-admin.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/StatusBadge';

const fc = (n: number) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n || 0);

export function TokensPage() {
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [stats, setStats] = useState<AdminTokenStats | null>(null);
  const [packages, setPackages] = useState<AdminTokenPackage[]>([]);
  const [services, setServices] = useState<AdminAIService[]>([]);
  const [transactions, setTransactions] = useState<AdminTokenTransaction[]>([]);
  const [activeTab, setActiveTab] = useState<'packages' | 'services' | 'transactions'>(
    'packages'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      tokenAdminService.getStats(),
      tokenAdminService.getPackages(),
      tokenAdminService.getAIServices(),
      tokenAdminService.getTransactions({ limit: 20 }),
    ])
      .then(([s, pkgs, svcs, txs]) => {
        setStats(s);
        setPackages(pkgs);
        setServices(svcs);
        setTransactions(txs);
      })
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleTogglePackage = async (pkg: AdminTokenPackage) => {
    try {
      await tokenAdminService.updatePackage(pkg.id, { isActive: !pkg.isActive });
      showSuccess('Estado actualizado');
      load();
    } catch (e: any) {
      showError(e.message || 'Error');
    }
  };

  const handleToggleService = async (svc: AdminAIService) => {
    try {
      await tokenAdminService.updateAIService(svc.id, { isActive: !svc.isActive });
      showSuccess('Estado actualizado');
      load();
    } catch (e: any) {
      showError(e.message || 'Error');
    }
  };

  const handleDeletePackage = async (pkg: AdminTokenPackage) => {
    if (!(await confirm({ message: `¿Eliminar "${pkg.name}"?`, danger: true }))) return;
    try {
      await tokenAdminService.deletePackage(pkg.id);
      showSuccess('Paquete eliminado');
      load();
    } catch (e: any) {
      showError(e.message || 'Error');
    }
  };

  const handleDeleteService = async (svc: AdminAIService) => {
    if (!(await confirm({ message: `¿Eliminar "${svc.name}"?`, danger: true }))) return;
    try {
      await tokenAdminService.deleteAIService(svc.id);
      showSuccess('Servicio eliminado');
      load();
    } catch (e: any) {
      showError(e.message || 'Error');
    }
  };

  if (loading && !stats) return <LoadingState message="Cargando tokens…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Tokens</h1>
        <p className="text-xs text-slate-500">Administración del monedero</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white rounded-xl shadow-sm p-3">
            <p className="text-[10px] text-slate-500">Vendidos</p>
            <p className="text-base font-bold text-primary">
              {stats.totalTokensSold.toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3">
            <p className="text-[10px] text-slate-500">Usados</p>
            <p className="text-base font-bold">
              {stats.totalTokensUsed.toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3">
            <p className="text-[10px] text-slate-500">Ingresos</p>
            <p className="text-base font-bold text-emerald-600">
              {fc(stats.totalRevenue)}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3">
            <p className="text-[10px] text-slate-500">Activos</p>
            <p className="text-base font-bold">{stats.activeUsers}</p>
          </div>
        </div>
      )}

      <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
        {(['packages', 'services', 'transactions'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`flex-1 py-2 rounded-md text-[11px] font-semibold transition-colors ${
              activeTab === t ? 'bg-primary text-white' : 'text-slate-600'
            }`}
          >
            {t === 'packages' ? 'Paquetes' : t === 'services' ? 'Servicios IA' : 'Tx'}
          </button>
        ))}
      </div>

      {activeTab === 'packages' && (
        <>
          {packages.length === 0 ? (
            <EmptyState icon="📦" title="Sin paquetes" />
          ) : (
            <ul className="space-y-2">
              {packages.map((p) => (
                <li key={p.id} className="bg-white rounded-xl shadow-sm p-3">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <p className="text-sm font-bold">{p.name}</p>
                      <p className="text-xs text-slate-500">
                        {p.tokens} tokens · {fc(p.price)}
                      </p>
                    </div>
                    <StatusBadge
                      status={p.isActive ? 'active' : 'inactive'}
                    />
                  </div>
                  {p.description && (
                    <p className="text-[11px] text-slate-500 mb-1.5">
                      {p.description}
                    </p>
                  )}
                  <div className="flex gap-1.5 pt-1.5 border-t border-slate-100">
                    <button
                      onClick={() => handleTogglePackage(p)}
                      className={`flex-1 py-1.5 rounded-md text-xs font-semibold ${
                        p.isActive
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-emerald-50 text-emerald-700'
                      }`}
                    >
                      {p.isActive ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      onClick={() => handleDeletePackage(p)}
                      className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs"
                    >
                      🗑️
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {activeTab === 'services' && (
        <>
          {services.length === 0 ? (
            <EmptyState icon="🤖" title="Sin servicios IA" />
          ) : (
            <ul className="space-y-2">
              {services.map((s) => (
                <li key={s.id} className="bg-white rounded-xl shadow-sm p-3">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <p className="text-sm font-bold">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.cost} tokens</p>
                    </div>
                    <StatusBadge status={s.isActive ? 'active' : 'inactive'} />
                  </div>
                  {s.description && (
                    <p className="text-[11px] text-slate-500 mb-1.5">
                      {s.description}
                    </p>
                  )}
                  <div className="flex gap-1.5 pt-1.5 border-t border-slate-100">
                    <button
                      onClick={() => handleToggleService(s)}
                      className={`flex-1 py-1.5 rounded-md text-xs font-semibold ${
                        s.isActive
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-emerald-50 text-emerald-700'
                      }`}
                    >
                      {s.isActive ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      onClick={() => handleDeleteService(s)}
                      className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs"
                    >
                      🗑️
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {activeTab === 'transactions' && (
        <>
          {transactions.length === 0 ? (
            <EmptyState icon="📊" title="Sin transacciones" />
          ) : (
            <ul className="space-y-2">
              {transactions.map((tx) => (
                <li key={tx.id} className="bg-white rounded-xl shadow-sm p-3">
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {tx.userName || 'Usuario'}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {tx.description}
                      </p>
                    </div>
                    <span
                      className={`text-sm font-bold flex-shrink-0 ${
                        tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                      }`}
                    >
                      {tx.amount > 0 ? '+' : ''}
                      {tx.amount}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400">
                    {tx.type} ·{' '}
                    {new Date(tx.createdAt).toLocaleDateString('es-MX')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}